// Talking to the tile server: index pixels in, class areas out.
//
// Both directions are driven by `indexClasses.ts`. The tiles are coloured by
// the class table as a TiTiler interval colormap, and the areas are counted by
// the SAME boundaries handed to TiTiler as histogram bins — so the number in
// the legend row and the colour beside it describe one thing.
//
// Everything here is a pure function of (config, asset, index). No fetching,
// so it can be unit-tested; the queries live in useIndexPixels.
import type { AnyIndexCode as ApiIndexCode } from "@/api/indices";
import { histogramBins, titilerColormap } from "./indexClasses";

/**
 * Per-index COG key. The pipeline writes one raster per index beside the raw
 * bands under the scene's AOI-hashed prefix (`build_asset_key` in
 * `app/modules/imagery/storage.py`), and registers them on the pgstac item.
 */
export function indexAssetKey(asset: { stac_item_id: string }, code: ApiIndexCode): string {
  return `${asset.stac_item_id}/${code}.tif`;
}

function assetUri(bucket: string, asset: { stac_item_id: string }, code: ApiIndexCode): string {
  return `s3://${bucket}/${indexAssetKey(asset, code)}`;
}

function trimTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

/**
 * How the tile server resamples when warping a 10 m raster onto a much finer
 * web-mercator tile — i.e. at every zoom a grower actually works at.
 *
 * Nearest gives literal 10 m squares, which read as a mosaic of boxes rather
 * than as a field. Bilinear interpolates the VALUES before they are
 * classified, so class edges follow the shape of the crop instead of the pixel
 * grid — and because interpolation happens in value space, the output still
 * contains ONLY class colours. Verified against prod: a tile rendered this way
 * decodes to the same four class colours as the nearest one, no blends.
 *
 * Note this is `reproject`, not `resampling`: the latter governs decimation on
 * READ and is a no-op when upsampling, which is the case here.
 *
 * The legend's areas are NOT affected — those come from /cog/statistics at
 * native resolution, so the numbers still describe the measured pixels while
 * the map draws their smoothed shape.
 */
const REPROJECT = "bilinear";

/**
 * Tile edge in pixels, for both the request and the MapLibre source.
 *
 * 512 rather than the 256 default because the cost here is per REQUEST, not
 * per pixel: measured against prod, one 512 tile renders in ~246ms while the
 * four 256 tiles covering the same ground cost ~1288ms in total and three
 * more round trips. A farm-wide view drops from ~92 tiles to ~25.
 */
export const TILE_SIZE = 512;

/**
 * How the tile server is ASKED for that size.
 *
 * TiTiler has no `tilesize` parameter. Its tile endpoint takes `scale`, and
 * computes `tilesize = scale * tms.tileWidth` — so 512 on the 256-wide
 * WebMercatorQuad is `scale=2`. Sending `tilesize=512` instead is not an
 * error anyone sees: FastAPI drops an unknown query parameter, the server
 * returns its 256 default, and only the picture is wrong.
 *
 * That is worth spelling out because of what MapLibre does with the answer.
 * A source declaring `tileSize: 512` fetches one pyramid level LOWER than a
 * 256 source at the same screen zoom, so each tile covers twice the ground
 * per axis. Handed a 256 image for it, MapLibre stretches that image over a
 * 512 footprint with `raster-resampling: nearest` — so every raster pixel is
 * drawn four times its area with hard edges, which is the mosaic of boxes the
 * `reproject` above exists to remove. Verified against the prod tile server:
 * `tilesize=512` returns a 256x256 PNG byte-identical to sending nothing,
 * while `scale=2` returns 512x512.
 */
const SCALE = String(TILE_SIZE / 256);

/**
 * XYZ template for one block's index raster, for a MapLibre raster source.
 *
 * `{z}/{x}/{y}` are left intact for MapLibre to interpolate. There is
 * deliberately NO `rescale`: rescale stretches values into 0–255 before the
 * colormap is applied, which would leave every interval in the class table
 * pointing at the wrong data. Pixels outside every interval — nodata, cloud —
 * come back transparent, so the satellite base shows through wherever there
 * is no reading, and the legend accounts for that area separately.
 */
export function blockTileUrl(input: {
  tileServerBaseUrl: string;
  s3Bucket: string;
  /** A block asset or a whole-farm raster — only the prefix is read. */
  asset: { stac_item_id: string };
  code: ApiIndexCode;
  /** Cut the tile to this AOI's boundary. See `cutlineParams`. */
  cutlineAoi?: string | null;
}): string {
  const params = new URLSearchParams({
    url: assetUri(input.s3Bucket, input.asset, input.code),
    colormap: titilerColormap(input.code),
    reproject: REPROJECT,
    scale: SCALE,
    ...cutlineParams(input.cutlineAoi),
  });
  return `${trimTrailingSlash(input.tileServerBaseUrl)}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?${params.toString()}`;
}

/**
 * Ask the tile server to cut what it renders to the farm's own outline.
 *
 * The stored raster is cut on its own 10 m pixel grid, so a pixel whose
 * centre is inside the farm is kept whole and colour runs up to 5 m past the
 * border — 15 m on the 30 m thermal product. `algorithm=cutline` cuts again
 * on the grid of the image being returned, which is screen pixels, so the
 * edge follows the boundary at any zoom.
 *
 * The boundary is looked up by AOI hash, which the pipeline writes as the
 * last segment of `stac_item_id` and publishes as `aoi/<hash>.geojson` beside
 * the rasters. A farm whose boundary is not published yet renders uncut, so
 * this can never empty the map.
 *
 * Passed to `/cog/statistics` as well, so one rule cuts both the picture and
 * the legend. Measured on a prod farm raster the areas do not move: the
 * statistics are read at the raster's own resolution, where the stored mask
 * already applied this rule. It is sent so the two cannot drift apart.
 */
export function cutlineParams(aoi: string | null | undefined): Record<string, string> {
  return aoi ? { algorithm: "cutline", algorithm_params: JSON.stringify({ aoi }) } : {};
}

/**
 * The AOI hash an asset was cut from — the last segment of its item id.
 *
 * `build_asset_key` (app/modules/imagery/storage.py) fixes that layout as
 * `{provider}/{product}/{scene}/{aoi_hash}`, and `indexAssetKey` already
 * depends on it.
 */
export function aoiHashFromStacItemId(stacItemId: string): string | null {
  const last = stacItemId.split("/").pop();
  return last && last.length >= 4 ? last : null;
}

/**
 * Statistics URL for one block's index raster, binned on the class edges.
 *
 * `max_size` overrides TiTiler's 1024px default, under which a larger raster
 * is decimated before counting — which would silently understate every area
 * on a big block. Real blocks here are ~50–500px square at 10m, so 4096 costs
 * nothing and removes the failure mode.
 */
export function blockStatsUrl(input: {
  tileServerBaseUrl: string;
  s3Bucket: string;
  asset: { stac_item_id: string };
  code: ApiIndexCode;
  /** Count only the pixels inside this AOI's boundary. */
  cutlineAoi?: string | null;
}): string {
  const params = new URLSearchParams({
    url: assetUri(input.s3Bucket, input.asset, input.code),
    histogram_bins: histogramBins(input.code).join(","),
    max_size: "4096",
    ...cutlineParams(input.cutlineAoi),
  });
  return `${trimTrailingSlash(input.tileServerBaseUrl)}/cog/statistics?${params.toString()}`;
}

/** The slice of TiTiler's /cog/statistics response this module reads. */
export interface BandStatistics {
  /** `[counts, edges]` — counts has one entry per class, edges has one more. */
  histogram: [number[], number[]];
  valid_pixels: number;
  masked_pixels: number;
  /** Mean over the valid pixels — the block's own reading for this scene. */
  mean: number;
}

export interface BlockPixelCounts {
  blockId: string;
  /** Pixel count per class, in the class table's own order (lowest first). */
  perClass: number[];
  /** Pixels with a reading. Equals the sum of `perClass`. */
  validPixels: number;
  /** Pixels inside the AOI with no usable reading — cloud, shadow, nodata. */
  maskedPixels: number;
  /** Square metres one pixel covers. */
  pixelAreaM2: number;
  /**
   * The block's mean for this index and scene. Taken from the same statistics
   * the areas come from, rather than from the map summary — which carries
   * only three of the seven indices, so a block would go uncoloured on the
   * other four.
   */
  meanValue: number;
}

/**
 * Read one block's statistics response into pixel counts per class.
 *
 * TiTiler keys statistics by band name; these rasters are single-band, so
 * take the first band rather than hard-coding "b1" — a renamed band would
 * otherwise silently zero the legend.
 */
export function readBlockCounts(
  blockId: string,
  body: Record<string, BandStatistics | undefined>,
  resolutionM: number,
): BlockPixelCounts | null {
  const band = Object.values(body).find((b): b is BandStatistics => Boolean(b?.histogram));
  if (!band) return null;
  const [counts] = band.histogram;
  return {
    blockId,
    perClass: counts,
    validPixels: band.valid_pixels,
    maskedPixels: band.masked_pixels,
    pixelAreaM2: resolutionM * resolutionM,
    meanValue: band.mean,
  };
}

/** The UTC calendar day an instant falls in, or NaN if it will not parse. */
function utcDay(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.NaN : Math.floor(t / 86_400_000);
}

/**
 * The farm surface to draw for a pass, or null to fall back to per-block.
 *
 * Judged against the DATE THE READER ASKED FOR, not against the blocks.
 *
 * It used to compare the farm raster's day with the first block asset's day,
 * on the reasoning that an api handing back the latest farm raster beside a
 * historical pass's block rasters would paint today's pixels under a timeline
 * reading two years ago. The api stopped doing that: `get_farm_scene_raster`
 * bounds the surface to the acquisition day of `at` and returns None for a
 * pass that has no surface of its own.
 *
 * Comparing against the blocks was then not just redundant but wrong, because
 * the two are no longer the same clock. A farm that moved to farm-level
 * fetching stops writing block ingestion jobs, so its block rows freeze on the
 * day of the cut-over while the farm surfaces carry on daily. On prod,
 * agrosina's Bashier Elkhier had a farm raster for 2026-08-25 and a newest
 * block job from 2026-08-10: fifteen days apart, so every pass was rejected
 * and the console drew 36 stale block rasters — 36 tile sources and 36
 * statistics calls — under a date bar saying 2026-08-25. Slow AND untrue.
 *
 * So: no requested date means "latest", and the latest surface is what that
 * means. A requested date accepts the surface for that day. Days, not
 * instants, because blocks in different Sentinel tiles are sensed minutes
 * apart for what a grower calls one pass.
 */
export function farmRasterForPass<T extends { scene_datetime: string }>(
  farm: T | null | undefined,
  /** The instant the console is reading as of, or null for "latest". */
  at: string | null | undefined,
): T | null {
  if (!farm) return null;
  if (!at) return farm;
  const requested = utcDay(at);
  const surface = utcDay(farm.scene_datetime);
  if (Number.isNaN(requested)) return farm;
  return Number.isNaN(surface) || surface !== requested ? null : farm;
}

export interface ClassAreaSummary {
  /** Square metres per class, in the class table's order (lowest first). */
  areaM2ByClass: number[];
  /** Total area carrying a reading. */
  coveredM2: number;
  /** Ground in scope with no reading for this scene. */
  noDataM2: number;
  /**
   * Whether these numbers describe the block that was asked for.
   *
   * False whenever no block was asked for, AND whenever one was asked for
   * that `counts` holds no row of — the whole-farm figures are returned
   * instead. The caller must label the panel from THIS rather than from the
   * block it selected, or the reader is told a farm's area belongs to one
   * block. See `summariseClassAreas` for when the second case happens.
   */
  scopedToBlock: boolean;
}

/**
 * Sum per-class areas across blocks.
 *
 * `scopeBlockId` narrows to one block; pass null for the whole farm.
 *
 * A block `counts` has no row for CANNOT be narrowed to, and the answer then
 * is the whole farm rather than nothing. Two ways to get there:
 *
 *  * A farm drawn from one stitched surface is measured once, under
 *    `FARM_SCOPE_ID` — there are no per-block rows to pick from at all, and
 *    on a farm cut over to farm-AOI fetching that is every pass. Filtering
 *    to a block matched nothing and the legend read 0.0 on every class:
 *    a farm with 219 measured passes reporting no land in any colour.
 *  * A block whose statistics call failed is absent from `counts`.
 *
 * Zero is the wrong answer to both. It is not "this block has no land in any
 * class"; it is "this reading is not held per block", and the honest thing
 * to show beside the colours is the farm they were measured over. The caller
 * reads `scopedToBlock` to say which it got.
 *
 * `farmAreaM2` is the ground the FARM occupies, and it exists because masked
 * pixels are not it. A COG is a rectangle; the raster inside it is clipped to
 * a polygon, so every pixel in the bounding box that the polygon misses
 * counts as masked. Reported raw, that says a farm has unread ground where it
 * has no ground at all — on Bashier Elkhier, 56.9 feddan of "no reading"
 * against a 203.4 feddan farm that only adds up if you include six feddan of
 * neighbouring desert. Given the farm's own area, the honest figure is what
 * is left of it after the readings. It is used only when the summary is
 * farm-wide; a block scope falls back to masked pixels, having no surveyed
 * area of its own to measure against.
 */
export function summariseClassAreas(
  counts: readonly BlockPixelCounts[],
  classCount: number,
  scopeBlockId: string | null,
  farmAreaM2?: number | null,
): ClassAreaSummary {
  const scopedToBlock = scopeBlockId !== null && counts.some((c) => c.blockId === scopeBlockId);
  const areaM2ByClass = new Array<number>(classCount).fill(0);
  let coveredM2 = 0;
  let maskedM2 = 0;
  for (const c of counts) {
    if (scopedToBlock && c.blockId !== scopeBlockId) continue;
    for (let i = 0; i < classCount; i += 1) {
      const area = (c.perClass[i] ?? 0) * c.pixelAreaM2;
      areaM2ByClass[i] += area;
      coveredM2 += area;
    }
    maskedM2 += c.maskedPixels * c.pixelAreaM2;
  }
  // Pixels straddling the boundary are counted whole, so coverage can edge
  // past the surveyed area — clamp rather than report negative unread ground.
  const noDataM2 =
    !scopedToBlock && farmAreaM2 && farmAreaM2 > 0 ? Math.max(0, farmAreaM2 - coveredM2) : maskedM2;
  return { areaM2ByClass, coveredM2, noDataM2, scopedToBlock };
}
